import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    Grid,
    Chip,
    Stack,
    CircularProgress,
    Alert,
    Container,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Badge,
    Button,
    Snackbar,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    TextField
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import BuildIcon from "@mui/icons-material/Build";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EuroIcon from "@mui/icons-material/Euro";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import InfoIcon from '@mui/icons-material/Info';
import { RentingsSameUser } from "../graphql/querys/rentingsSameUser";
import { CreateGT } from "../graphql/mutation/createGT";
import { CreateGD } from "../graphql/mutation/createGD";
import { UpdateMachine } from "../graphql/mutation/updateMachine";
import TOConlineAuth from "../components/TOConlineAuth";
import PDFGeneratorServiceFn from "../components/PdfGenerator_rel";
import { useTranslation } from "react-i18next";

// Add TOConline auth status query
import { gql } from "@apollo/client";

const AUTH_STATUS_QUERY = gql`
    query GetTOConlineAuthStatus {
        toconlineAuthStatus {
            authenticated
            token_info {
                token_type
                expires_at
                expires_in_seconds
                is_expired
            }
            error
        }
    }
`;

export default function RentingsByUserScreen() {
    const { t } = useTranslation();
    const { loading, error, data, refetch } = useQuery(RentingsSameUser, {
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
        notifyOnNetworkStatusChange: true,
    });
    const [createGT, { loading: createGTLoading }] = useMutation(CreateGT);
    const [createGD, { loading: createGDLoading }] = useMutation(CreateGD);
    const [updateMachine] = useMutation(UpdateMachine);
    const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });
    const [machineSelectionOpen, setMachineSelectionOpen] = useState(false);
    const [selectedMachines, setSelectedMachines] = useState([]);
    const [currentUserGroup, setCurrentUserGroup] = useState(null);
    const [selectedGtGroup, setSelectedGtGroup] = useState('');
    const [gdAddressForm, setGdAddressForm] = useState({ shipmentAddressDetail: '', shipmentPostcode: '', shipmentCity: '', shipmentCountry: 'PT' });
    const [processingStep, setProcessingStep] = useState(''); // To track the current processing step

    const [gtCreationDialog, setGtCreationDialog] = useState({ open: false, userGroup: null, machines: [], atUser: '', atPassword: '', vehicleRegistration: '', shipmentAddressDetail: '', shipmentPostcode: '', shipmentCity: '', shipmentCountry: 'PT', expectedShipmentUnloadingTime: '' });
    // machines will be an array of copies of machine info to edit: { _id, name, ...original }

    // Add TOConline auth status query
    const { data: authStatusData } = useQuery(AUTH_STATUS_QUERY);

    // Refetch on mount and whenever the tab/window regains focus
    useEffect(() => {
        refetch();
        const onFocus = () => refetch();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [refetch]);

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={60} />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Alert severity="error">
                    {t("RENTINGS_BY_USER.LOAD_ERROR", { message: error.message })}
                </Alert>
            </Container>
        );
    }

    const rentingsData = data?.rentingsSameUser || [];

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("pt-PT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat("pt-PT", {
            style: "currency",
            currency: "EUR",
        }).format(price);
    };

    const getGtGroupKey = (renting) => {
        return renting.gtDocumentId || renting.gtDocumentNo || "";
    };

    const buildEditableMachineSelection = (machines) => {
        return machines.map((renting) => ({
            ...renting,
            editableDescription: renting.machineInfo?.name || "",
        }));
    };

    const getEligibleGdGroups = (userGroup) => {
        const eligibleMachines = (userGroup?.activeRentings || []).filter(
            (renting) => renting.hasGT && !renting.hasGD
        );

        const groupsMap = new Map();
        eligibleMachines.forEach((renting) => {
            const groupKey = getGtGroupKey(renting);
            if (!groupKey) return;

            if (!groupsMap.has(groupKey)) {
                groupsMap.set(groupKey, {
                    key: groupKey,
                    label: renting.gtDocumentNo || renting.gtDocumentId || t("RENTINGS_BY_USER.GT_NO_REFERENCE"),
                    machines: [],
                });
            }

            groupsMap.get(groupKey).machines.push(renting);
        });

        return Array.from(groupsMap.values());
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case "active":
            case "ativo":
                return "success";
            case "pending":
            case "pendente":
                return "warning";
            case "finished":
            case "terminado":
                return "default";
            default:
                return "info";
        }
    };

    const handleCreateGTClick = (userGroup, event) => {
        event.stopPropagation();
        // Check TOConline authentication status
        const isAuthenticated = authStatusData?.toconlineAuthStatus?.authenticated;
        const isTokenExpired = authStatusData?.toconlineAuthStatus?.token_info?.is_expired;

        if (!isAuthenticated || isTokenExpired) {
            setSnackbar({
                open: true,
                message: t("RENTINGS_BY_USER.AUTH_REQUIRED_GT"),
                severity: "warning"
            });
            return;
        }

        const activeRentings = userGroup.activeRentings.filter(r => !r.hasGT);

        if (activeRentings.length === 0) {
            setSnackbar({
                open: true,
                message: t("RENTINGS_BY_USER.NO_GT_RENTINGS"),
                severity: "warning"
            });
            return;
        }

        setGtCreationDialog({
            open: true,
            userGroup: userGroup,
            atUser: '',
            atPassword: '',
            vehicleRegistration: '',
            shipmentAddressDetail: userGroup.userInfo?.addressInfo?.Address || '',
            shipmentPostcode: userGroup.userInfo?.addressInfo?.postalCode || '',
            shipmentCity: userGroup.userInfo?.localidade || '',
            shipmentCountry: userGroup.userInfo?.addressInfo?.country || 'PT',
            expectedShipmentUnloadingTime: '',
            machines: activeRentings.map(r => ({
                rentingId: r._id,
                machineId: r.machineInfo._id,
                originalName: r.machineInfo.name,
                name: r.machineInfo.name,
                ref: r.machineInfo.ref,
                selected: true,
            }))
        });
    };

    const handleCloseGtDialog = () => {
        setGtCreationDialog({ open: false, userGroup: null, machines: [], atUser: '', atPassword: '', vehicleRegistration: '', shipmentAddressDetail: '', shipmentPostcode: '', shipmentCity: '', shipmentCountry: 'PT', expectedShipmentUnloadingTime: '' });
        setProcessingStep('');
    };

    const handleProceedCreateGT = async () => {
        const { userGroup, machines, atUser, atPassword, vehicleRegistration, shipmentAddressDetail, shipmentPostcode, shipmentCity, shipmentCountry, expectedShipmentUnloadingTime } = gtCreationDialog;
        if (!userGroup) return;
        const selectedGtMachines = machines.filter((machine) => machine.selected);

        if (selectedGtMachines.length === 0) {
            setSnackbar({
                open: true,
                message: t("RENTINGS_BY_USER.SELECT_MACHINE_GT"),
                severity: "warning"
            });
            return;
        }

        if (!vehicleRegistration?.trim()) {
            setSnackbar({
                open: true,
                message: t("RENTINGS_BY_USER.VEHICLE_REQUIRED"),
                severity: "error"
            });
            return;
        }
        if (!shipmentAddressDetail?.trim() || !shipmentPostcode?.trim() || !shipmentCity?.trim()) {
            setSnackbar({
                open: true,
                message: t("RENTINGS_BY_USER.UNLOAD_REQUIRED"),
                severity: "error"
            });
            return;
        }
        setProcessingStep('creating_gt');

        try {
            // 1. Update machine names if changed
            const updatePromises = selectedGtMachines
                .filter(m => m.name !== m.originalName)
                .map(m => updateMachine({
                    variables: {
                        input: {
                            _id: m.machineId,
                            name: m.name
                        }
                    }
                }));

            if (updatePromises.length > 0) {
                await Promise.all(updatePromises);
            }

            // 2. Create GT with the (potentially new) names
            // Transform the data to match the input structure
            // Remove __typename from addressInfo to avoid GraphQL validation errors
            const cleanAddressInfo = userGroup.userInfo.addressInfo ? {
                Address: userGroup.userInfo.addressInfo.Address,
                postalCode: userGroup.userInfo.addressInfo.postalCode
            } : undefined;

            const input = {
                userId: userGroup.userId,
                userInfo: {
                    _id: userGroup.userInfo._id,
                    email: userGroup.userInfo.email,
                    name: userGroup.userInfo.name,
                    nif: userGroup.userInfo.nif,
                    addressInfo: cleanAddressInfo,
                    localidade: userGroup.userInfo.localidade
                },
                atUser,
                atPassword,
                vehicleRegistration: vehicleRegistration.trim(),
                shipmentAddressDetail: shipmentAddressDetail || undefined,
                shipmentPostcode: shipmentPostcode || undefined,
                shipmentCity: shipmentCity || undefined,
                shipmentCountry: shipmentCountry || undefined,
                expectedShipmentUnloadingTime,
                activeRentings: userGroup.activeRentings
                    .filter((renting) => selectedGtMachines.some((machine) => machine.rentingId === renting._id))
                    .map(renting => {
                    // Find the updated name from the dialog state for this renting/machine
                    const machineState = selectedGtMachines.find(m => m.rentingId === renting._id);
                    const currentName = machineState ? machineState.name : renting.machineInfo.name;

                    return {
                        _id: renting._id,
                        pricePerDay: renting.pricePerDay,
                        client: renting.client,
                        machine: renting.machine,
                        machineInfo: {
                            _id: renting.machineInfo._id,
                            name: currentName, // Use the updated name
                            ref: renting.machineInfo.ref
                        },
                        item_code: renting.machineInfo?.ref || "",
                        status: renting.status,
                        startedRent: renting.startedRent,
                        endedRent: renting.endedRent,
                        previsionEndingRent: renting.previsionEndingRent
                    };
                }),
                totalActiveRentings: selectedGtMachines.length
            };

            const result = await createGT({
                variables: { userRentingGroup: input }
            });

            // Show success message
            setSnackbar({
                open: true,
                message: t("RENTINGS_BY_USER.GT_CREATED", { name: userGroup.userInfo.name }),
                severity: "success"
            });

            // Open the public link
            openDocumentPublicLink(result.data.createGT, userGroup.userInfo.name, 'GT');

            // Close dialog
            handleCloseGtDialog();

            // Refresh data
            refetch();

        } catch (error) {
            console.error('Error creating GT flow:', error);
            setSnackbar({
                open: true,
                message: t("RENTINGS_BY_USER.GT_PROCESS_ERROR", { message: error.message }),
                severity: "error"
            });
            setProcessingStep(''); // Reset loading on error
        }
    };

    // Handle machine selection for return guide
    const handleMachineSelection = (machine) => {
        setSelectedMachines(prev => {
            if (prev.some(m => m._id === machine._id)) {
                return prev.filter(m => m._id !== machine._id);
            } else {
                return [...prev, { ...machine, editableDescription: machine.editableDescription || machine.machineInfo?.name || '' }];
            }
        });
    };

    const handleSelectedMachineDescriptionChange = (machineId, value) => {
        setSelectedMachines((prev) =>
            prev.map((machine) =>
                machine._id === machineId
                    ? { ...machine, editableDescription: value }
                    : machine
            )
        );
    };

    // Open machine selection dialog
    const openMachineSelectionDialog = (userGroup, event) => {
        // Prevent accordion expansion when clicking the button
        event.stopPropagation();

        const eligibleGroups = getEligibleGdGroups(userGroup);
        const defaultGroup = eligibleGroups[0];
        const groupMachines = defaultGroup?.machines || [];
        const gtFallback = groupMachines.find(renting =>
            renting.gtShipmentAddressDetail || renting.gtShipmentPostcode || renting.gtShipmentCity || renting.gtShipmentCountry
        );

        setCurrentUserGroup(userGroup);
        setSelectedGtGroup(defaultGroup?.key || '');
        setSelectedMachines(buildEditableMachineSelection(groupMachines));
        setGdAddressForm({
            shipmentAddressDetail: userGroup.userInfo?.addressInfo?.Address || gtFallback?.gtShipmentAddressDetail || '',
            shipmentPostcode: userGroup.userInfo?.addressInfo?.postalCode || gtFallback?.gtShipmentPostcode || '',
            shipmentCity: userGroup.userInfo?.localidade || gtFallback?.gtShipmentCity || '',
            shipmentCountry: userGroup.userInfo?.addressInfo?.country || gtFallback?.gtShipmentCountry || 'PT'
        });
        setMachineSelectionOpen(true);
    };

    const handleGtGroupChange = (groupKey) => {
        if (!currentUserGroup) return;

        const eligibleGroups = getEligibleGdGroups(currentUserGroup);
        const selectedGroup = eligibleGroups.find((group) => group.key === groupKey);
        const groupMachines = selectedGroup?.machines || [];
        const gtFallback = groupMachines.find(renting =>
            renting.gtShipmentAddressDetail || renting.gtShipmentPostcode || renting.gtShipmentCity || renting.gtShipmentCountry
        );

        setSelectedGtGroup(groupKey);
        setSelectedMachines(buildEditableMachineSelection(groupMachines));
        setGdAddressForm({
            shipmentAddressDetail: currentUserGroup.userInfo?.addressInfo?.Address || gtFallback?.gtShipmentAddressDetail || '',
            shipmentPostcode: currentUserGroup.userInfo?.addressInfo?.postalCode || gtFallback?.gtShipmentPostcode || '',
            shipmentCity: currentUserGroup.userInfo?.localidade || gtFallback?.gtShipmentCity || '',
            shipmentCountry: currentUserGroup.userInfo?.addressInfo?.country || gtFallback?.gtShipmentCountry || 'PT'
        });
    };

    // Close machine selection dialog
    const handleCloseSelection = () => {
        setMachineSelectionOpen(false);
        setSelectedMachines([]);
        setCurrentUserGroup(null);
        setSelectedGtGroup('');
        setGdAddressForm({ shipmentAddressDetail: '', shipmentPostcode: '', shipmentCity: '', shipmentCountry: 'PT' });
    };

    // Create GD with selected machines
    const handleCreateGDWithSelection = async () => {
        if (!currentUserGroup || selectedMachines.length === 0) {
            setSnackbar({
                open: true,
                message: t("RENTINGS_BY_USER.SELECT_MACHINE_GD"),
                severity: "warning"
            });
            return;
        }

        if (!gdAddressForm.shipmentAddressDetail?.trim() || !gdAddressForm.shipmentPostcode?.trim() || !gdAddressForm.shipmentCity?.trim()) {
            setSnackbar({
                open: true,
                message: t("RENTINGS_BY_USER.GD_ADDRESS_REQUIRED"),
                severity: "error"
            });
            return;
        }

        // Check TOConline authentication status
        const isAuthenticated = authStatusData?.toconlineAuthStatus?.authenticated;
        const isTokenExpired = authStatusData?.toconlineAuthStatus?.token_info?.is_expired;

        if (!isAuthenticated || isTokenExpired) {
            setSnackbar({
                open: true,
                message: t("RENTINGS_BY_USER.AUTH_REQUIRED_GD"),
                severity: "warning"
            });
            return;
        }

        try {
            setProcessingStep('creating_gd');

            // Transform the data to match the input structure, but only include selected machines
            // Remove __typename from addressInfo to avoid GraphQL validation errors
            const cleanAddressInfo = currentUserGroup.userInfo.addressInfo ? {
                Address: currentUserGroup.userInfo.addressInfo.Address,
                postalCode: currentUserGroup.userInfo.addressInfo.postalCode
            } : undefined;

            const input = {
                userId: currentUserGroup.userId,
                userInfo: {
                    _id: currentUserGroup.userInfo._id,
                    email: currentUserGroup.userInfo.email,
                    name: currentUserGroup.userInfo.name,
                    nif: currentUserGroup.userInfo.nif,
                    addressInfo: cleanAddressInfo,
                    localidade: currentUserGroup.userInfo.localidade
                },
                shipmentAddressDetail: gdAddressForm.shipmentAddressDetail.trim(),
                shipmentPostcode: gdAddressForm.shipmentPostcode.trim(),
                shipmentCity: gdAddressForm.shipmentCity.trim(),
                shipmentCountry: gdAddressForm.shipmentCountry || 'PT',
                activeRentings: selectedMachines.map(renting => ({
                    _id: renting._id,
                    pricePerDay: renting.pricePerDay,
                    client: renting.client,
                    machine: renting.machine,
                    machineInfo: {
                        _id: renting.machineInfo._id,
                        name: renting.editableDescription?.trim() || renting.machineInfo.name,
                        ref: renting.machineInfo.ref
                    },
                    gtDocumentNo: renting.gtDocumentNo,
                    gtDocumentId: renting.gtDocumentId,
                    item_code: renting.machineInfo?.ref || "", // Adicionando ref como item_code
                    status: renting.status,
                    startedRent: renting.startedRent,
                    endedRent: renting.endedRent,
                    previsionEndingRent: renting.previsionEndingRent
                })),
                totalActiveRentings: selectedMachines.length
            };

            const result = await createGD({
                variables: { userRentingGroup: input }
            });

            console.log('GD Document:', result.data.createGD);

            // Open the public link (document is stored in DB by backend)
            openDocumentPublicLink(result.data.createGD, currentUserGroup.userInfo.name, 'GD');

            // Generate PDF summary for the stopped rentals
            generateRentalSummaryPDF(selectedMachines, currentUserGroup.userInfo.name);

            // Show success message
            setSnackbar({
                open: true,
                message: t("RENTINGS_BY_USER.GD_CREATED", { name: currentUserGroup.userInfo.name }),
                severity: "success"
            });

            // Close dialog and reset selection
            handleCloseSelection();

            // Refresh data to update GD status and rental status
            refetch();

        } catch (error) {
            console.error('Error creating GD or stopping rentals:', error);
            setSnackbar({
                open: true,
                message: t("RENTINGS_BY_USER.GD_PROCESS_ERROR", { message: error.message }),
                severity: "error"
            });
        } finally {
            setProcessingStep('');
        }
    };

    // Generate and download a PDF summary of the rentals that were stopped
    const generateRentalSummaryPDF = (rentals, clientName) => {
        // Format rental data for PDF generation
        const pdfData = rentals.map(rental => {
            // Calculate rental duration in days
            const startDate = new Date(rental.startedRent);
            const endDate = new Date(); // Current date as the end date
            const durationMs = endDate - startDate;
            const durationDays = Math.ceil(durationMs / (1000 * 60 * 60 * 24));

            // Calculate total price
            const totalPrice = rental.pricePerDay * durationDays;

            return {
                [t("FIELDS.machine")]: rental.machineInfo?.name || t("COMMON.NA"),
                [t("FIELDS.ref")]: rental.machineInfo?.ref || t("COMMON.NA"),
                [t("FIELDS.startDate")]: formatDate(rental.startedRent),
                [t("RENTING.END_DATE")]: formatDate(new Date()),
                [t("RENTINGS_BY_USER.DURATION_DAYS")]: durationDays,
                [t("FIELDS.pricePerDay")]: `${rental.pricePerDay.toFixed(2)}€`,
                [t("RENTINGS_BY_USER.TOTAL_VALUE")]: `${totalPrice.toFixed(2)}€`
            };
        });

        // Generate the PDF
        const title = t("RENTINGS_BY_USER.RENTAL_SUMMARY_TITLE", {
            name: clientName.replace(/\s+/g, '_'),
            date: new Date().toISOString().split('T')[0],
        });
        PDFGeneratorServiceFn.generatePDF(pdfData, title);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const currentEligibleGdGroups = currentUserGroup ? getEligibleGdGroups(currentUserGroup) : [];
    const currentGdGroup = currentEligibleGdGroups.find((group) => group.key === selectedGtGroup);

    // Old standalone edit handlers removed

    const openDocumentPublicLink = (docData, userName, type) => {
        const publicLink =
            docData?.api_response?.public_link ||
            docData?.public_link ||
            docData?.gtPublicLink ||
            docData?.gdPublicLink;

        if (publicLink) {
            window.open(publicLink, "_blank", "noopener");
        } else {
            setSnackbar({
                open: true,
                message: t("RENTINGS_BY_USER.PUBLIC_LINK_MISSING", { type, name: userName }),
                severity: "warning"
            });
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, pb: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {t("RENTINGS_BY_USER.TITLE")}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                    {t("RENTINGS_BY_USER.SUBTITLE")}
                </Typography>
            </Box>

            {/* TOConline Integration */}
            <Box sx={{ mb: 4, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                    {t("RENTINGS_BY_USER.TOCONLINE_AUTH_TITLE")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t("RENTINGS_BY_USER.TOCONLINE_AUTH_DESC")}
                    {!authStatusData?.toconlineAuthStatus?.authenticated &&
                        t("RENTINGS_BY_USER.TOCONLINE_AUTH_CONNECT_HINT")}
                </Typography>
                <TOConlineAuth />
            </Box>

            {rentingsData.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: "center", py: 6 }}>
                        <BusinessIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            {t("RENTINGS_BY_USER.NO_ACTIVE_RENTINGS")}
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Stack spacing={3}>
                    {rentingsData.map((userGroup) => (
                        <Accordion key={userGroup.userId} defaultExpanded>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel-${userGroup.userId}-content`}
                                id={`panel-${userGroup.userId}-header`}
                            >
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: "100%" }}>
                                    <Avatar
                                        src={userGroup.userInfo?.avatar}
                                        sx={{ width: 48, height: 48 }}
                                    >
                                        <PersonIcon />
                                    </Avatar>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="h6" component="div">
                                            {userGroup.userInfo?.name || t("RENTINGS_BY_USER.UNNAMED_USER")}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {userGroup.userInfo?.email}
                                        </Typography>
                                        {userGroup.userInfo?.phoneNumber && (
                                            <Typography variant="body2" color="text.secondary">
                                                {t("RENTINGS_BY_USER.PHONE_SHORT", { phone: userGroup.userInfo.phoneNumber })}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Tooltip title={
                                            userGroup.totalActiveRentings === 0
                                                ? t("RENTINGS_BY_USER.GT_TOOLTIP_NO_ACTIVE")
                                                : t("RENTINGS_BY_USER.GT_TOOLTIP_CREATE")
                                        }>
                                            <span>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    startIcon={<DescriptionIcon />}
                                                    onClick={(event) => handleCreateGTClick(userGroup, event)}
                                                    disabled={createGTLoading || processingStep === 'creating_gt' || userGroup.totalActiveRentings === 0 || userGroup.activeRentings.every(r => r.hasGT)}
                                                    sx={{ minWidth: 'auto' }}
                                                >
                                                    {createGTLoading || processingStep === 'creating_gt' ? t("RENTINGS_BY_USER.CREATING") : t("RENTINGS_BY_USER.CREATE_GT")}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={
                                            userGroup.totalActiveRentings === 0
                                                ? t("RENTINGS_BY_USER.GD_TOOLTIP_NO_ACTIVE")
                                                : !userGroup.activeRentings.some(r => r.hasGT)
                                                    ? t("RENTINGS_BY_USER.GD_TOOLTIP_NEEDS_GT")
                                                    : userGroup.activeRentings.every(r => r.hasGD)
                                                        ? t("RENTINGS_BY_USER.GD_TOOLTIP_ALL_CREATED")
                                                        : t("RENTINGS_BY_USER.GD_TOOLTIP_CREATE")
                                        }>
                                            <span>
                                                <Button
                                                    variant="contained"
                                                    color="secondary"
                                                    size="small"
                                                    startIcon={<AssignmentReturnIcon />}
                                                    onClick={(event) => openMachineSelectionDialog(userGroup, event)}
                                                    disabled={
                                                        createGDLoading ||
                                                        userGroup.totalActiveRentings === 0 ||
                                                        !userGroup.activeRentings.some(r => r.hasGT) ||
                                                        userGroup.activeRentings.every(r => r.hasGD)
                                                    }
                                                    sx={{ minWidth: 'auto' }}
                                                >
                                                    {createGDLoading ? t("RENTINGS_BY_USER.CREATING") : t("RENTINGS_BY_USER.CREATE_GD")}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                        <Badge badgeContent={userGroup.totalActiveRentings} color="primary">
                                            <Chip
                                                icon={<BuildIcon />}
                                                label={t("RENTINGS_BY_USER.MACHINE_COUNT", { count: userGroup.totalActiveRentings })}
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </Badge>
                                    </Stack>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                {userGroup.activeRentings && userGroup.activeRentings.length > 0 ? (
                                    <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: "divider" }}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>{t("FIELDS.machine")}</TableCell>
                                                    <TableCell>{t("FIELDS.ref")}</TableCell>
                                                    <TableCell>{t("FIELDS.category")}</TableCell>
                                                    <TableCell>{t("FIELDS.pricePerDay")}</TableCell>
                                                    <TableCell>{t("FIELDS.startDate")}</TableCell>
                                                    <TableCell>{t("FIELDS.expectedEndDate")}</TableCell>
                                                    <TableCell>{t("FIELDS.status")}</TableCell>
                                                    <TableCell>{t("FIELDS.gt")}</TableCell>
                                                    <TableCell>{t("FIELDS.gd")}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {userGroup.activeRentings.map((renting) => (
                                                    <TableRow key={renting._id} hover>
                                                        <TableCell>
                                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                                {renting.machineInfo?.photo && (
                                                                    <Avatar
                                                                        src={renting.machineInfo.photo}
                                                                        sx={{ width: 40, height: 40 }}
                                                                        variant="rounded"
                                                                    />
                                                                )}
                                                                <Box>
                                                                    <Typography variant="subtitle2">
                                                                        {renting.machineInfo?.name || t("COMMON.NA")}
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight="medium">
                                                                {renting.machineInfo?.ref || t("COMMON.NA")}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {renting.machineInfo?.category || t("COMMON.NA")}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                <EuroIcon fontSize="small" color="success" />
                                                                <Typography variant="body2" fontWeight="medium">
                                                                    {formatPrice(renting.pricePerDay)}
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                <CalendarTodayIcon fontSize="small" color="action" />
                                                                <Typography variant="body2">
                                                                    {formatDate(renting.startedRent)}
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {formatDate(renting.previsionEndingRent)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={renting.status ? t(`STATUS.${renting.status}`, renting.status) : t("COMMON.NA")}
                                                                color={getStatusColor(renting.status)}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={renting.hasGT ? t("STATUS.CREATED") : t("STATUS.PENDING")}
                                                                color={renting.hasGT ? "success" : "warning"}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={renting.hasGD ? t("STATUS.CREATED") : (renting.hasGT ? t("STATUS.PENDING") : t("STATUS.AWAITING_GT"))}
                                                                color={renting.hasGD ? "success" : (renting.hasGT ? "warning" : "default")}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        {t("RENTINGS_BY_USER.NO_ACTIVE_RENTINGS_USER")}
                                    </Alert>
                                )}

                                {(() => {
                                    const addrInfo = userGroup.userInfo?.addressInfo || {};
                                    const hasContact =
                                        userGroup.userInfo?.email ||
                                        userGroup.userInfo?.phoneNumber ||
                                        userGroup.userInfo?.customer_tax_registration_number ||
                                        userGroup.userInfo?.nif ||
                                        addrInfo.Address ||
                                        addrInfo.address ||
                                        addrInfo.postalCode ||
                                        userGroup.userInfo?.localidade;

                                    if (!hasContact) return null;

                                    return (
                                        <Box sx={{ mt: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                {t("RENTINGS_BY_USER.CONTACT_INFO")}
                                            </Typography>
                                            <Grid container spacing={2}>
                                                {userGroup.userInfo?.email && (
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t("FIELDS.email")}: {userGroup.userInfo.email}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                {userGroup.userInfo?.phoneNumber && (
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t("FIELDS.phone")}: {userGroup.userInfo.phoneNumber}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                {(userGroup.userInfo?.nif || userGroup.userInfo?.customer_tax_registration_number) && (
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t("FIELDS.nif")}: {userGroup.userInfo.nif || userGroup.userInfo.customer_tax_registration_number}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                {(addrInfo.Address || addrInfo.address) && (
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t("FIELDS.address")}: {addrInfo.Address || addrInfo.address}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                {addrInfo.postalCode && (
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t("FIELDS.postalCode")}: {addrInfo.postalCode}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                {userGroup.userInfo?.localidade && (
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t("FIELDS.localidade")}: {userGroup.userInfo.localidade}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Box>
                                    );
                                })()}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Stack>
            )}

            {/* Machine Selection Dialog */}
            <Dialog
                open={machineSelectionOpen}
                onClose={handleCloseSelection}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {t("RENTINGS_BY_USER.SELECT_GD_MACHINES_TITLE")}
                </DialogTitle>
                <DialogContent dividers>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {t("RENTINGS_BY_USER.SELECT_GD_MACHINES_INFO")}
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {t("RENTINGS_BY_USER.CANCEL_RENT_NOTE")}
                        </Typography>
                    </Alert>

                    {currentEligibleGdGroups.length > 1 && (
                        <TextField
                            fullWidth
                            select
                            SelectProps={{ native: true }}
                            label={t("FIELDS.gtSubgroup")}
                            value={selectedGtGroup}
                            onChange={(e) => handleGtGroupChange(e.target.value)}
                            sx={{ mb: 2 }}
                        >
                            {currentEligibleGdGroups.map((group) => (
                                <option key={group.key} value={group.key}>
                                    {group.label} ({t("RENTINGS_BY_USER.SELECTED_MACHINES", { count: group.machines.length })})
                                </option>
                            ))}
                        </TextField>
                    )}

                    {currentGdGroup && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {t("RENTINGS_BY_USER.CREATING_GD_FOR_GT", { gt: currentGdGroup.label })}
                        </Alert>
                    )}

                    {currentUserGroup && currentUserGroup.activeRentings && (
                        <List>
                            {(currentGdGroup?.machines || [])
                                .map((renting) => (
                                    <React.Fragment key={renting._id}>
                                        <ListItem>
                                            <ListItemIcon>
                                                <Checkbox
                                                    edge="start"
                                                    checked={selectedMachines.some(m => m._id === renting._id)}
                                                    onChange={() => handleMachineSelection(renting)}
                                                    color="primary"
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="subtitle1">
                                                        {renting.machineInfo?.name || t("RENTINGS_BY_USER.UNNAMED_MACHINE")}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Stack direction="column" spacing={0.5}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t("MOBILE_MACHINE.REF", { ref: renting.machineInfo?.ref || t("COMMON.NA") })}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t("FIELDS.startDate")}: {formatDate(renting.startedRent)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t("FIELDS.pricePerDay")}: {formatPrice(renting.pricePerDay)}
                                                        </Typography>
                                                        {selectedMachines.some((machine) => machine._id === renting._id) && (
                                                            <TextField
                                                                fullWidth
                                                                label={t("FIELDS.description")}
                                                                size="small"
                                                                value={selectedMachines.find((machine) => machine._id === renting._id)?.editableDescription || renting.machineInfo?.name || ''}
                                                                onChange={(e) => handleSelectedMachineDescriptionChange(renting._id, e.target.value)}
                                                            />
                                                        )}
                                                    </Stack>
                                                }
                                            />
                                        </ListItem>
                                        <Divider />
                                    </React.Fragment>
                                ))}
                        </List>
                    )}

                    {selectedMachines.length > 0 && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Chip
                                icon={<InfoIcon />}
                                label={t("RENTINGS_BY_USER.SELECTED_MACHINES", { count: selectedMachines.length })}
                                color="primary"
                            />
                        </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t("RENTINGS_BY_USER.GD_ADDRESS_TITLE")}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t("FIELDS.address")}
                                value={gdAddressForm.shipmentAddressDetail}
                                onChange={(e) => setGdAddressForm({ ...gdAddressForm, shipmentAddressDetail: e.target.value })}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label={t("FIELDS.postalCode")}
                                value={gdAddressForm.shipmentPostcode}
                                onChange={(e) => setGdAddressForm({ ...gdAddressForm, shipmentPostcode: e.target.value })}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label={t("FIELDS.city")}
                                value={gdAddressForm.shipmentCity}
                                onChange={(e) => setGdAddressForm({ ...gdAddressForm, shipmentCity: e.target.value })}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label={t("FIELDS.country")}
                                value={gdAddressForm.shipmentCountry}
                                onChange={(e) => setGdAddressForm({ ...gdAddressForm, shipmentCountry: e.target.value })}
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseSelection}>{t("ACTIONS.CANCEL")}</Button>
                    <Button
                        onClick={handleCreateGDWithSelection}
                        variant="contained"
                        color="secondary"
                        disabled={selectedMachines.length === 0 || processingStep !== ''}
	                    >
	                        {processingStep === 'creating_gd' ? t("RENTINGS_BY_USER.CREATING_GD") : t("RENTINGS_BY_USER.CREATE_GD_AND_CLOSE")}
	                    </Button>
                </DialogActions>
            </Dialog>

            {/* GT Creation Dialog with Edit Capability */}
            <Dialog open={gtCreationDialog.open} onClose={handleCloseGtDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {t("RENTINGS_BY_USER.CONFIRM_GT_TITLE")}
                    <Typography variant="body2" color="text.secondary">
                        {t("RENTINGS_BY_USER.CONFIRM_GT_DESC")}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <List>
                        {gtCreationDialog.machines.map((machine, index) => (
                            <ListItem key={machine.rentingId || index}>
                                <Stack direction="column" sx={{ width: '100%' }} spacing={1}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Checkbox
                                            checked={!!machine.selected}
                                            onChange={(e) => {
                                                const newMachines = [...gtCreationDialog.machines];
                                                newMachines[index].selected = e.target.checked;
                                                setGtCreationDialog({ ...gtCreationDialog, machines: newMachines });
                                            }}
                                        />
                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                                            {t("MOBILE_MACHINE.REF", { ref: machine.ref || t("COMMON.NA") })}
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            label={t("FIELDS.machineDescription")}
                                            value={machine.name || ""}
                                            onChange={(e) => {
                                                const newMachines = [...gtCreationDialog.machines];
                                                newMachines[index].name = e.target.value;
                                                setGtCreationDialog({ ...gtCreationDialog, machines: newMachines });
                                            }}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Box>
                                </Stack>
                            </ListItem>
                        ))}
                    </List>

                    <Box sx={{ px: 2, pb: 1 }}>
                        <Chip
                            icon={<InfoIcon />}
                            label={t("RENTINGS_BY_USER.SELECTED_MACHINES_GT", {
                                count: gtCreationDialog.machines.filter((machine) => machine.selected).length,
                            })}
                            color="primary"
                            variant="outlined"
                        />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1, px: 2 }}>
                        {t("RENTINGS_BY_USER.UNLOAD_DATA_TITLE")}
                    </Typography>
                    <Grid container spacing={2} sx={{ px: 2, pb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t("FIELDS.vehicleRegistration")}
                                placeholder="Ex: 12-AB-34"
                                value={gtCreationDialog.vehicleRegistration}
                                onChange={(e) => setGtCreationDialog({ ...gtCreationDialog, vehicleRegistration: e.target.value })}
                                variant="outlined"
                                size="small"
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t("FIELDS.unloadingAddress")}
                                placeholder="Ex: Rua Exemplo, 123"
                                value={gtCreationDialog.shipmentAddressDetail}
                                onChange={(e) => setGtCreationDialog({ ...gtCreationDialog, shipmentAddressDetail: e.target.value })}
                                variant="outlined"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t("FIELDS.unloadingPostcode")}
                                placeholder="Ex: 0000-000"
                                value={gtCreationDialog.shipmentPostcode}
                                onChange={(e) => setGtCreationDialog({ ...gtCreationDialog, shipmentPostcode: e.target.value })}
                                variant="outlined"
                                size="small"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t("FIELDS.unloadingCity")}
                                placeholder="Ex: Lisboa"
                                value={gtCreationDialog.shipmentCity}
                                onChange={(e) => setGtCreationDialog({ ...gtCreationDialog, shipmentCity: e.target.value })}
                                variant="outlined"
                                size="small"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t("FIELDS.unloadingTime")}
                                type="datetime-local"
                                value={gtCreationDialog.expectedShipmentUnloadingTime}
                                onChange={(e) => setGtCreationDialog({ ...gtCreationDialog, expectedShipmentUnloadingTime: e.target.value })}
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t("FIELDS.unloadingCountry")}
                                placeholder="Ex: PT"
                                value={gtCreationDialog.shipmentCountry}
                                onChange={(e) => setGtCreationDialog({ ...gtCreationDialog, shipmentCountry: e.target.value })}
                                variant="outlined"
                                size="small"
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1, px: 2 }}>
                        {t("RENTINGS_BY_USER.AT_CREDENTIALS")}
                    </Typography>
                    <Grid container spacing={2} sx={{ px: 2, pb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t("FIELDS.atUser")}
                                placeholder="Ex: 123456789/1"
                                value={gtCreationDialog.atUser}
                                onChange={(e) => setGtCreationDialog({ ...gtCreationDialog, atUser: e.target.value })}
                                variant="outlined"
                                size="small"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t("FIELDS.atPassword")}
                                type="password"
                                value={gtCreationDialog.atPassword}
                                onChange={(e) => setGtCreationDialog({ ...gtCreationDialog, atPassword: e.target.value })}
                                variant="outlined"
                                size="small"
                                required
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseGtDialog} disabled={processingStep !== ''}>{t("ACTIONS.CANCEL")}</Button>
                    <Button
                        onClick={handleProceedCreateGT}
                        variant="contained"
                        color="primary"
                        disabled={processingStep !== ''}
                        startIcon={processingStep === 'creating_gt' ? <CircularProgress size={20} color="inherit" /> : <DescriptionIcon />}
                    >
                        {processingStep === 'creating_gt' ? t("RENTINGS_BY_USER.PROCESSING") : t("RENTINGS_BY_USER.CONFIRM_AND_CREATE_GT")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}
